import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/db.ts';
import { TrendingUp, Users, DollarSign, Calendar, Copy, CheckCircle, AlertCircle } from 'lucide-react';

// Date formatting helper
const formatDistanceToNow = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) {
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  } else if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  } else if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else {
    return 'just now';
  }
};

interface AffiliateStats {
  success: boolean;
  error?: string;
  affiliate_code?: string;
  summary?: {
    total_invitees: number;
    total_bets: number;
    total_bet_volume: number;
    total_earnings: number;
  };
  invitees?: Array<{
    id: string;
    email: string;
    username?: string;
    joined_at: string;
    bet_count: number;
    total_bet_amount: number;
    commission_earned: number;
  }>;
  recent_earnings?: Array<{
    id: string;
    created_at: string;
    bet_amount: number;
    commission_amount: number;
    invitee_email: string;
    invitee_username?: string;
    market_question: string;
  }>;
}

export const AffiliateDashboard: React.FC = () => {
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [timeRange, setTimeRange] = useState<'all' | 'month' | 'week'>('all');

  useEffect(() => {
    fetchAffiliateStats();
  }, [timeRange]);

  const fetchAffiliateStats = async () => {
    try {
      setLoading(true);
      setError(null);

      let startDate = null;
      const now = new Date();
      
      if (timeRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      } else if (timeRange === 'week') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }

      const { data, error } = await supabase.rpc('get_affiliate_stats', {
        p_start_date: startDate,
        p_end_date: null
      });

      if (error) throw error;
      
      setStats(data);
    } catch (err: Error | unknown) {
      console.error('Error fetching affiliate stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load affiliate statistics');
    } finally {
      setLoading(false);
    }
  };

  const copyAffiliateLink = () => {
    if (stats?.affiliate_code) {
      const link = `${window.location.origin}/auth/register?ref=${stats.affiliate_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !stats?.success) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <p className="text-red-800">{error || stats?.error || 'Failed to load affiliate data'}</p>
      </div>
    );
  }

  const summary = stats.summary || { total_invitees: 0, total_bets: 0, total_bet_volume: 0, total_earnings: 0 };

  return (
    <div className="space-y-6">
      {/* Affiliate Link Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Affiliate Link</h3>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3 min-w-0">
          <input
            type="text"
            readOnly
            value={`${window.location.origin}/auth/register?ref=${stats.affiliate_code}`}
            className="min-w-0 w-full sm:flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
          />
          <button
            type="button"
            onClick={copyAffiliateLink}
            className="shrink-0 w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            {copied ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy Link
              </>
            )}
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Share this link to invite users. You'll earn 1% commission on all their trades!
        </p>
      </div>

      {/* Time Range Filter */}
      <div className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-gray-500" />
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as 'all' | 'month' | 'week')}
          className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">All Time</option>
          <option value="month">Last 30 Days</option>
          <option value="week">Last 7 Days</option>
        </select>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invitees</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_invitees}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Trades</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_bets}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Trade Volume</p>
              <p className="text-2xl font-bold text-gray-900">R{summary.total_bet_volume.toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">R{summary.total_earnings.toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Invitees Table */}
      {stats.invitees && stats.invitees.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Your Invitees</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trades
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trade Volume
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Your Earnings
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.invitees.map((invitee) => (
                  <tr key={invitee.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {invitee.username || invitee.email.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-500">{invitee.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDistanceToNow(new Date(invitee.joined_at))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invitee.bet_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      R{invitee.total_bet_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      R{invitee.commission_earned.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Earnings */}
      {stats.recent_earnings && stats.recent_earnings.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Recent Earnings</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {stats.recent_earnings.map((earning) => (
              <div key={earning.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {earning.invitee_username || earning.invitee_email.split('@')[0]} placed a trade
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {earning.market_question}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistanceToNow(new Date(earning.created_at))}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm text-gray-600">Trade: R{earning.bet_amount.toFixed(2)}</p>
                    <p className="text-sm font-medium text-green-600">+R{earning.commission_amount.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty States */}
      {(!stats.invitees || stats.invitees.length === 0) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No invitees yet</h3>
          <p className="text-gray-600">
            Share your affiliate link to start earning commissions!
          </p>
        </div>
      )}
    </div>
  );
}; 